import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ExternalLink, QrCode } from "lucide-react";
import SpinningGlobe from "./SpinningGlobe";
import { Separator } from "@/components/ui/separator";

const Footer = () => {
  const { t } = useTranslation();
  return (
    <footer id="site-footer" className="bg-slate-900 text-white py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Brand */}
          <div className="lg:col-span-3">
            <div className="flex items-center space-x-2 mb-4">
              <img
                src="/labor-ed-shield-v3.png"
                alt="Union Strong"
                className="w-14 h-14 object-contain drop-shadow-[0_0_10px_rgba(59,130,246,0.4)]"
              />
              <span className="text-xl font-bold font-serif">Union Strong</span>
            </div>
            <p className="text-slate-400 text-sm">{t("footer.tagline")}</p>
            <img
              src="https://customer-assets.emergentagent.com/job_13e4391a-3898-46d5-8331-1b28fb5b216e/artifacts/w8fuslq6_IMG_4150.png"
              alt="Union Strong Hero"
              className="h-36 w-auto mt-4 drop-shadow-[0_0_20px_rgba(220,38,38,0.25)]"
              style={{ animation: "float 4s ease-in-out infinite" }}
            />
          </div>

          {/* Full sitemap */}
          <div className="lg:col-span-9 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 text-sm">
            <div>
              <h4 className="font-semibold mb-3 text-white">Explore</h4>
              <ul className="space-y-2 text-slate-400">
                <li><Link to="/unions" className="hover:text-white transition-colors">{t("nav.findUnions")}</Link></li>
                <li><Link to="/compare" className="hover:text-white transition-colors">{t("nav.compare")}</Link></li>
                <li><Link to="/strikes" className="hover:text-white transition-colors">Strikes</Link></li>
                <li><Link to="/scholarships" className="hover:text-white transition-colors">Scholarships</Link></li>
                <li><Link to="/print-shop" className="hover:text-white transition-colors">Print Shop</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-3 text-white">Learn &amp; Tools</h4>
              <ul className="space-y-2 text-slate-400">
                <li><Link to="/learn" className="hover:text-white transition-colors">{t("nav.learn")}</Link></li>
                <li><Link to="/steward-training" className="hover:text-white transition-colors">Steward Training</Link></li>
                <li><Link to="/learn/financial-literacy" className="hover:text-white transition-colors">Financial Literacy</Link></li>
                <li><Link to="/learn/benefits-calculator" className="hover:text-white transition-colors">Benefits Calculator</Link></li>
                <li><Link to="/learn/know-your-rights" className="hover:text-white transition-colors">Know Your Rights</Link></li>
                <li><Link to="/fair-price" className="hover:text-amber-400 transition-colors">Fair Price Watch</Link></li>
                <li><Link to="/academy" className="hover:text-amber-400 transition-colors">Labor Education Academy — 7 Free Courses</Link></li>
                <li><Link to="/job-readiness" className="hover:text-white transition-colors">Job Readiness</Link></li>
                <li><Link to="/learn/legislative-tracker" className="hover:text-white transition-colors">Legislative Tracker</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-3 text-white">Community &amp; Media</h4>
              <ul className="space-y-2 text-slate-400">
                <li><Link to="/labor-and-the-pulpit" className="hover:text-amber-400 transition-colors">Labor &amp; the Pulpit</Link></li>
                <li><Link to="/four-pillars" className="hover:text-amber-400 transition-colors">The Four Pillars of Black Labor Power</Link></li>
                <li><Link to="/we-refuse-to-be-divided" className="hover:text-amber-400 transition-colors">We Refuse to Be Divided</Link></li>
                <li><Link to="/labor-and-ai" className="hover:text-cyan-400 transition-colors">Labor &amp; AI · AFL-CIO Resolution</Link></li>
                <li><Link to="/data-centers" className="hover:text-cyan-400 transition-colors">Data Centers 101 · Regulate, Don't Retreat</Link></li>
                <li><Link to="/business-hub" className="hover:text-cyan-400 transition-colors">Local Business Hub</Link></li>
                <li><Link to="/negotiation-school" className="hover:text-cyan-400 transition-colors">Negotiation School — Free Course</Link></li>
                <li><Link to="/friends-of-labor" className="hover:text-amber-400 transition-colors">Friends of Labor · Who's Who 2026</Link></li>
                <li><Link to="/back-to-work" className="hover:text-amber-400 transition-colors">Back To Work · Reentry Hub</Link></li>
                <li><Link to="/wellness" className="hover:text-amber-400 transition-colors">360 Health Wellness Union Group Discount</Link></li>
                <li><Link to="/united-way" className="hover:text-amber-400 transition-colors">United Way of the Mid-South</Link></li>
                <li><Link to="/i-am-a-man" className="hover:text-amber-400 transition-colors">I AM A MAN · Civil Rights Museum</Link></li>
                <li><Link to="/media" className="hover:text-white transition-colors">Media</Link></li>
                <li><Link to="/digital-media" className="hover:text-amber-400 transition-colors">Union Digital Publications</Link></li>
                <li><Link to="/gallery" className="hover:text-white transition-colors">Gallery</Link></li>
                <li><Link to="/book" className="hover:text-white transition-colors">Book</Link></li>
                <li><Link to="/youth-library" className="hover:text-white transition-colors">Youth Library</Link></li>
                <li><Link to="/chat" className="hover:text-white transition-colors">Ask President Bradshaw</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-3 text-white">Take Action</h4>
              <ul className="space-y-2 text-slate-400">
                <li><Link to="/voter-hub" className="hover:text-white transition-colors">Southern Voter Power Hub</Link></li>
                <li><Link to="/jobs" className="hover:text-white transition-colors">Union Jobs</Link></li>
                <li><Link to="/sponsor" className="hover:text-amber-400 transition-colors font-medium">Become a Sponsor</Link></li>
                <li><Link to="/the-south" className="hover:text-amber-400 transition-colors font-bold text-red-400 break-words leading-tight block">#TheSouthGotSomethingToSay</Link></li>
                <li><a href="https://aflcio.org/formaunion/contact" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors flex items-center gap-1">Join a Union <ExternalLink className="w-3 h-3" /></a></li>
                <li><Link to="/working-families-party" className="hover:text-orange-400 transition-colors" data-testid="footer-wfp">Working Families Party · Run for Office</Link></li>
                <li><Link to="/share" className="hover:text-white transition-colors flex items-center gap-1"><QrCode className="w-4 h-4" />{t("footer.shareApp")}</Link></li>
                <li><Link to="/contact" className="hover:text-white transition-colors font-medium text-blue-300" data-testid="footer-contact-us">Contact Us · Leave a Message</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-3 text-white">{t("footer.resources")}</h4>
              <ul className="space-y-2 text-slate-400">
                <li><a href="https://www.nlrb.gov" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">NLRB</a></li>
                <li><a href="https://www.dol.gov" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Dept. of Labor</a></li>
                <li><a href="https://aflcio.org" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">AFL-CIO</a></li>
                <li><a href="https://www.solidaritycenter.org/connect/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors flex items-center gap-1" data-testid="footer-solidarity-center">Solidarity Center <ExternalLink className="w-3 h-3" /></a></li>
              </ul>
            </div>
          </div>
        </div>
        <Separator className="my-8 bg-slate-700" />
        {/* World globe language showcase */}
        <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-10 mb-8" data-testid="footer-globe-showcase">
          <button
            onClick={() => window.dispatchEvent(new Event("open-language-menu"))}
            className="flex-shrink-0 hover:scale-105 transition-transform"
            aria-label="Choose your language"
            data-testid="footer-globe"
          >
            <span className="globe-footer rounded-full inline-block">
              <SpinningGlobe size="w-44 h-44 md:w-52 md:h-52" />
            </span>
          </button>
          <div className="text-center md:text-left max-w-xl">
            <p className="font-black text-white text-lg font-serif mb-1">One World. One Movement.</p>
            <p className="text-slate-400 text-sm mb-2">Every page of this site can be read in 19 languages:</p>
            <p className="text-slate-300 text-xs leading-relaxed">
              English · Español · Tiếng Việt · 中文 · العربية · Soomaali · 한국어 · Français · Deutsch ·
              Português · 日本語 · Tagalog · हिन्दी · မြန်မာ · Русский · Kreyòl Ayisyen · فارسی · اردو · አማርኛ
            </p>
            <button
              onClick={() => window.dispatchEvent(new Event("open-language-menu"))}
              className="mt-3 bg-blue-600 hover:bg-blue-500 text-white text-xs font-black px-4 py-2 rounded-full transition-colors"
              data-testid="footer-globe-translate-btn"
            >
              CHOOSE YOUR LANGUAGE
            </button>
          </div>
        </div>
        <Separator className="my-8 bg-slate-700" />
        <div className="flex justify-center mb-4">
          <img
            src="/share/bradshaw-solutions-logo-white.png"
            alt="Bradshaw Solutions union bug"
            className="h-7 opacity-60"
            data-testid="footer-union-bug"
          />
        </div>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-slate-500 text-sm">
          <p data-testid="footer-copyright">© {new Date().getFullYear()} {t("footer.copyright")}</p>
          <Link to="/terms" className="text-slate-400 hover:text-white transition-colors">Terms of Service</Link>
          <Link to="/privacy" className="text-slate-400 hover:text-white transition-colors" data-testid="footer-privacy-link">Privacy Policy</Link>
          <a
            href="https://www.facebook.com/share/1CU8g5F6QZ/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-slate-400 hover:text-blue-500 transition-colors"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
            </svg>
            {t("footer.followFacebook")}
          </a>
          <div className="flex items-center gap-2">
            <span>{t("footer.developedBy")}</span>
            <img
              src="https://customer-assets.emergentagent.com/job_labor-choice/artifacts/02x3d4a9_D980B255-06CB-4BA5-ADC8-EA2FD2C03B3A.png"
              alt="Bradshaw Solutions"
              className="w-8 h-8 rounded object-cover"
            />
            <span className="text-slate-400">Bradshaw Solutions</span>
          </div>
        </div>
        <p className="text-center text-slate-400 text-xs mt-4 leading-relaxed" data-testid="footer-founder-credit">
          <span className="text-slate-200 font-semibold">Kevin Bradshaw</span>
          <span className="hidden sm:inline"> · </span>
          <span className="block sm:inline">President, Memphis &amp; West Tennessee Central Labor Council, AFL-CIO</span>
          <span className="hidden sm:inline"> · </span>
          <span className="block sm:inline text-amber-400/90">Founder, LaborEducation.org</span>
        </p>
      </div>
    </footer>
  );
};

export default Footer;
export { Footer };
